import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Customer, Finding, ScanResult } from "../types/customer";
import type { Job } from "../types/job";
import { Header } from "../components/Header";
import { CustomerTable } from "../components/CustomerTable";
import { CustomerDetailModal } from "../components/CustomerDetailModal";
import { CustomerEditModal } from "../components/CustomerEditModal";
import { ReportModal } from "../components/ReportModal";
import { SettingsModal } from "../components/SettingsModal";
import { useToast } from "@/hooks/use-toast";
import { Helmet } from "@/components/Helmet";
import { formatDateTime, type TimezoneMode } from "../utils/dates";
import { apiBaseUrl, apiUrl, assertApiOk, fetchAdminState, fetchApi, fetchApiJson } from "../utils/api";
import { generateSecurityReport } from "../utils/pdfReportGenerator";
import { Button } from "@/components/ui/button";

type ReportMeta = {
  exists: boolean;
  updatedAt?: string;
  scanTimestamp?: string;
  size?: number;
};

const getPublicScanApiBase = (): string => {
  const fromEnv = import.meta.env.VITE_PUBLIC_SCAN_API_BASE as string | undefined;
  if (fromEnv) return fromEnv;
  if (typeof document !== "undefined") {
    const meta = document.querySelector<HTMLMetaElement>(
      'meta[name="public-scan-api-base"]',
    );
    const value = meta?.content?.trim();
    if (value && value !== "undefined" && !value.includes("%VITE_")) {
      return value;
    }
  }
  return "https://scan-api.shadowscan.com.au";
};

const Index = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCustomerId, setActiveCustomerId] = useState<string | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [timezone, setTimezone] = useState<TimezoneMode>("local");
  const [reportMetaByCustomerId, setReportMetaByCustomerId] = useState<Record<string, ReportMeta>>({});
  const [pdfGeneratingByCustomerId, setPdfGeneratingByCustomerId] = useState<Record<string, boolean>>({});
  const [leadFilter, setLeadFilter] = useState<"all" | "customers" | "leads">("all");
  const [sortMode, setSortMode] = useState<"default" | "recent">("default");
  const publicScanBase = useMemo(() => getPublicScanApiBase(), []);
  const { toast } = useToast();
  const prevJobStatusByIdRef = useRef<Map<string, string>>(new Map());
  const pendingPdfKeysRef = useRef<Set<string>>(new Set()); // `${customerId}|${scanTimestamp}`
  const pendingPdfBackoffRef = useRef<Map<string, { attempts: number; nextTryAt: number }>>(new Map());
  const pdfAutoBusyRef = useRef(false);
  const mountedAtMsRef = useRef(Date.now());
  const { data: adminState, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["autoscanAdminState"],
    queryFn: fetchAdminState,
    refetchInterval: (query) => {
      if (query.state.status !== "success") return false;
      const currentJobs = query.state.data?.jobs ?? [];
      const hasRunning = currentJobs.some((j) => j.status === "running");
      return hasRunning ? 1500 : 5000;
    },
  });

  const refreshState = useCallback(async () => {
    const result = await refetch();
    if (result.error) throw result.error;
    return result.data;
  }, [refetch]);

  useEffect(() => {
    if (!adminState) return;
    setCustomers(adminState.customers || []);
    setScanResults(adminState.scanResults || []);
    setJobs(adminState.jobs || []);
  }, [adminState]);

  const activeCustomer = useMemo(() => {
    if (!activeCustomerId) return null;
    return customers.find((c) => c.id === activeCustomerId) ?? null;
  }, [activeCustomerId, customers]);

  const runningJobs = useMemo(() => jobs.filter((j) => j.status === "running"), [jobs]);
  const isRunning = runningJobs.length > 0;
  const queueCount = customers.filter((c) => c.status === "scanning").length;
  const ipAddress = typeof window !== "undefined" ? window.location.hostname : undefined;

  const runStartedAt = useMemo(() => {
    if (runningJobs.length === 0) return undefined;
    const earliest = runningJobs
      .map((j) => new Date(j.startedAt).getTime())
      .filter((t) => Number.isFinite(t))
      .sort((a, b) => a - b)[0];
    if (!earliest) return undefined;
    return new Date(earliest);
  }, [runningJobs]);

  const debugApi = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("debugApi");
  const apiBase = apiBaseUrl();
  const apiStateUrl = apiUrl("/internal/admin/state");

  const extractDomain = useCallback((value: string) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const withProto = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    try {
      return new URL(withProto).hostname;
    } catch {
      return raw.replace(/^https?:\/\//i, "").split("/")[0] || "";
    }
  }, []);

  const startPublicScan = useCallback(
    async (customer: Customer) => {
      const domain = extractDomain(customer.url || customer.name || "");
      if (!domain) {
        throw new Error("Missing target domain for public scan.");
      }
      const res = await fetch(`${publicScanBase.replace(/\/+$/, "")}/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errorMessage = String((payload as { error?: string }).error || "");
        throw new Error(errorMessage || `Public scan failed (${res.status})`);
      }
      const jobId = String((payload as { jobId?: string }).jobId || "");
      if (!jobId) {
        throw new Error("Public scan did not return a job id.");
      }
      return jobId;
    },
    [extractDomain, publicScanBase],
  );

  useEffect(() => {
    if (!debugApi) return;
    console.info("[autoscan] api base:", apiBase);
    console.info("[autoscan] api state url:", apiStateUrl);
  }, [apiBase, apiStateUrl, debugApi]);

  const labelScanner = useCallback((scanner: string) => {
    const s = String(scanner || "").toLowerCase();
    if (s === "sitecheck") return "Sitecheck";
    if (s === "httpx") return "httpx";
    if (s === "wpscan") return "WPScan";
    if (s === "nuclei") return "Nuclei";
    return scanner;
  }, []);

  const fetchReportMeta = useCallback(async (customerId: string) => {
    const meta = await fetchApiJson<ReportMeta>(`/api/reports/${encodeURIComponent(customerId)}/meta`);
    setReportMetaByCustomerId((prev) => ({ ...prev, [customerId]: meta }));
    return meta;
  }, []);

  const openPdfReport = useCallback((customerId: string) => {
    window.open(apiUrl(`/api/reports/${encodeURIComponent(customerId)}/latest`), "_blank", "noopener,noreferrer");
  }, []);

  const buildScanGroup = useCallback(
    (customerId: string, scanTimestamp: string) => {
      const group = scanResults.filter((r) => r.customerId === customerId && r.timestamp === scanTimestamp);
      const findings = group.flatMap((r) => (Array.isArray(r.findings) ? r.findings : []));
      return { group, findings };
    },
    [scanResults],
  );

  const generateAndUploadPdf = useCallback(
    async (
      customer: Customer,
      {
        force = false,
        openAfter = false,
        silent = false,
      }: { force?: boolean; openAfter?: boolean; silent?: boolean } = {},
    ): Promise<"generated" | "skipped" | "pending"> => {
      const scanTimestamp = customer.lastScan;
      if (!scanTimestamp) {
        if (!silent) toast({ title: "No scan yet", description: "Run a scan before generating a PDF report." });
        return "skipped";
      }

      const { group, findings } = buildScanGroup(customer.id, scanTimestamp);
      if (group.length === 0) {
        // State polling may deliver the customer update before scan-results are visible; retry on next tick.
        return "pending";
      }

      const existing = reportMetaByCustomerId[customer.id];
      if (!force && existing?.exists && existing?.scanTimestamp === scanTimestamp) {
        if (openAfter) openPdfReport(customer.id);
        return "skipped";
      }

      setPdfGeneratingByCustomerId((prev) => ({ ...prev, [customer.id]: true }));
      try {
        const summary = {
          totalFindings: findings.length,
          critical: findings.filter((f) => f.severity === "critical").length,
          high: findings.filter((f) => f.severity === "high").length,
          medium: findings.filter((f) => f.severity === "medium").length,
          low: findings.filter((f) => f.severity === "low").length,
          info: findings.filter((f) => f.severity === "info").length,
        };
        const scannersUsed = Array.from(new Set(group.map((r) => r.scanner))).map(labelScanner);
        const blob = (await generateSecurityReport(customer, findings, summary, {
          scannersUsed,
          scanTimestamp,
          scanResults: group,
          output: "blob",
        })) as Blob;

        const uploadPath = `/api/reports/${encodeURIComponent(customer.id)}/latest?scanTimestamp=${encodeURIComponent(scanTimestamp)}`;
        const up = await fetchApi(uploadPath, {
          method: "PUT",
          headers: { "Content-Type": "application/pdf" },
          body: blob,
        });
        await assertApiOk(up, "Upload");
        await fetchReportMeta(customer.id).catch(() => {});
        if (!silent) toast({ title: "PDF ready", description: `Report generated for ${customer.name}.` });
        if (openAfter) openPdfReport(customer.id);
        return "generated";
      } finally {
        setPdfGeneratingByCustomerId((prev) => ({ ...prev, [customer.id]: false }));
      }
    },
    [buildScanGroup, fetchReportMeta, labelScanner, openPdfReport, reportMetaByCustomerId, toast],
  );

  const schedulePdfRetry = useCallback((key: string) => {
    const now = Date.now();
    const entry = pendingPdfBackoffRef.current.get(key) || { attempts: 0, nextTryAt: now };
    const attempts = entry.attempts + 1;
    if (attempts >= 3) {
      pendingPdfBackoffRef.current.delete(key);
      pendingPdfKeysRef.current.delete(key);
      return false;
    }
    const delayMs = Math.min(60_000, 5_000 * attempts);
    pendingPdfBackoffRef.current.set(key, { attempts, nextTryAt: now + delayMs });
    return true;
  }, []);

  // Auto-generate a PDF when a job transitions running -> finished (non-dry-run).
  useEffect(() => {
    const prev = prevJobStatusByIdRef.current;
    for (const job of jobs) {
      const prevStatus = prev.get(job.id);
      prev.set(job.id, job.status);
      if (prevStatus !== "running") continue;
      if (job.status !== "finished") continue;
      if (job.mode === "dry_run") continue;
      const finishedAtMs = job.finishedAt ? new Date(job.finishedAt).getTime() : NaN;
      if (!Number.isFinite(finishedAtMs) || finishedAtMs < mountedAtMsRef.current) continue;
      const customerId = job.customerId;
      const customer = customers.find((c) => c.id === customerId);
      const scanTimestamp = customer?.lastScan;
      if (!customer || !scanTimestamp) continue;
      const key = `${customerId}|${scanTimestamp}`;
      pendingPdfKeysRef.current.add(key);
      pendingPdfBackoffRef.current.set(key, { attempts: 0, nextTryAt: Date.now() });
    }
  }, [customers, generateAndUploadPdf, jobs, schedulePdfRetry]);

  // Drain any pending PDFs once scanResults catch up (avoids missing generation when state arrives out of order).
  useEffect(() => {
    if (pendingPdfKeysRef.current.size === 0) return;
    if (pdfAutoBusyRef.current) return;
    const keys = Array.from(pendingPdfKeysRef.current);
    for (const key of keys) {
      const backoff = pendingPdfBackoffRef.current.get(key);
      if (backoff && Date.now() < backoff.nextTryAt) continue;

      const [customerId, scanTimestamp] = key.split("|");
      if (!customerId || !scanTimestamp) {
        pendingPdfBackoffRef.current.delete(key);
        pendingPdfKeysRef.current.delete(key);
        continue;
      }
      const customer = customers.find((c) => c.id === customerId);
      if (!customer || customer.lastScan !== scanTimestamp) {
        pendingPdfBackoffRef.current.delete(key);
        pendingPdfKeysRef.current.delete(key);
        continue;
      }

      pdfAutoBusyRef.current = true;
      generateAndUploadPdf(customer, { silent: true })
        .then((result) => {
          if (result !== "pending") {
            pendingPdfBackoffRef.current.delete(key);
            pendingPdfKeysRef.current.delete(key);
          } else {
            // Ensure we don't spin tightly if the backend state is lagging.
            pendingPdfBackoffRef.current.set(key, { attempts: 0, nextTryAt: Date.now() + 2_000 });
          }
        })
        .catch(() => {
          schedulePdfRetry(key);
        })
        .finally(() => {
          pdfAutoBusyRef.current = false;
        });
      break; // one at a time
    }
  }, [customers, generateAndUploadPdf, scanResults, schedulePdfRetry]);

  // When opening modals, refresh report meta for the active customer so buttons reflect the latest state.
  useEffect(() => {
    if (!activeCustomerId) return;
    if (!detailModalOpen && !reportModalOpen) return;
    fetchReportMeta(activeCustomerId).catch(() => {});
  }, [activeCustomerId, detailModalOpen, fetchReportMeta, reportModalOpen]);

  const handleAdd = () => {
    setActiveCustomerId(null);
    setIsNewCustomer(true);
    setEditModalOpen(true);
  };

  const handleRefresh = () => {
    refreshState()
      .then(() =>
        toast({
          title: "Refreshed",
          description: "Loaded latest state from backend.",
        }),
      )
      .catch((error) =>
        toast({
          title: "Refresh failed",
          description: String(error?.message || error),
          variant: "destructive",
        }),
      );
  };

  const handleClearAllResults = () => {
    fetchApi("/api/clear", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scope: "all" }),
    })
      .then(async (r) => {
        await assertApiOk(r, "Clear");
        await refreshState();
        toast({
          title: "Cleared",
          description: "All results and logs were cleared.",
        });
      })
      .catch((error) =>
        toast({
          title: "Clear failed",
          description: String(error?.message || error),
          variant: "destructive",
        }),
      );
  };

  const handleScanAll = () => {
    customers.filter((c) => c.enabled).forEach((c) => handleScan(c));
  };

  const handleDryRunAll = () => {
    customers.filter((c) => c.enabled).forEach((c) => handleDryRun(c));
  };

  const handleCancel = () => {
    const running = jobs.filter((j) => j.status === "running");
    Promise.all(
      running.map((j) =>
        fetchApi("/api/cancel", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ jobId: j.id }),
        }).then((r) => assertApiOk(r, "Cancel")),
      ),
    )
      .then(() => refreshState())
      .then(() =>
        toast({
          title: "Cancelled",
          description: "All running jobs have been cancelled",
          variant: "destructive",
        }),
      )
      .catch((error) =>
        toast({
          title: "Cancel failed",
          description: String(error?.message || error),
          variant: "destructive",
        }),
      );
  };

  const handleOpenCustomer = (customer: Customer) => {
    setActiveCustomerId(customer.id);
    setReportModalOpen(false);
    setDetailModalOpen(true);
  };

  const handleMoreCustomer = (customer: Customer) => {
    setActiveCustomerId(customer.id);
    setDetailModalOpen(false);
    setReportModalOpen(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setActiveCustomerId(customer.id);
    setIsNewCustomer(false);
    setDetailModalOpen(false);
    setEditModalOpen(true);
  };

  const handleDeleteCustomer = (customer: Customer) => {
    fetchApi(`/api/customers/${customer.id}`, { method: "DELETE" })
      .then(async (r) => {
        await assertApiOk(r, "Delete");
        await refreshState();
        setDetailModalOpen(false);
        setReportModalOpen(false);
        setEditModalOpen(false);
        setActiveCustomerId(null);
        toast({
          title: "Customer deleted",
          description: `${customer.name} has been removed.`,
        });
      })
      .catch((error) =>
        toast({
          title: "Delete failed",
          description: String(error?.message || error),
          variant: "destructive",
        }),
      );
  };

  const handleClearCustomerResults = (customer: Customer) => {
    fetchApi("/api/clear", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scope: "customer", customerId: customer.id }),
    })
      .then(async (r) => {
        await assertApiOk(r, "Clear");
        await refreshState();
        toast({
          title: "Cleared",
          description: `Results and logs cleared for ${customer.name}.`,
        });
      })
      .catch((error) =>
        toast({
          title: "Clear failed",
          description: String(error?.message || error),
          variant: "destructive",
        }),
      );
  };

  const handleSaveCustomer = (customer: Customer) => {
    setActiveCustomerId(customer.id);
    const method = isNewCustomer ? "POST" : "PUT";
    const url = isNewCustomer ? "/api/customers" : `/api/customers/${customer.id}`;
    fetchApi(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(customer),
    })
      .then(async (r) => {
        await assertApiOk(r, "Save");
        await refreshState();
        toast({
          title: isNewCustomer ? "Customer added" : "Customer updated",
          description: `${customer.name} saved successfully`,
        });
      })
      .catch((error) =>
        toast({
          title: "Save failed",
          description: String(error?.message || error),
          variant: "destructive",
        }),
      );
  };

  const handleScan = async (customer: Customer) => {
    try {
      const data = await fetchApiJson<{ jobId?: string; error?: string }>("/api/scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId: customer.id, mode: "scan" }),
      });
      await refreshState();
      toast({ title: "Scan started", description: `Job ${data.jobId} started for ${customer.name}` });
    } catch (error) {
      const message = String(error?.message || error);
      if (message.includes("(404)") || message.includes("(405)") || message.includes("(501)") || message.includes("not_found")) {
        try {
          const jobId = await startPublicScan(customer);
          toast({
            title: "Public scan started",
            description: `Job ${jobId} started for ${customer.name}.`,
          });
          return;
        } catch (fallbackError) {
          toast({
            title: "Scan failed",
            description: String(fallbackError?.message || fallbackError),
            variant: "destructive",
          });
          return;
        }
      }
      toast({
        title: "Scan failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleDryRun = async (customer: Customer) => {
    try {
      const data = await fetchApiJson<{ jobId?: string; error?: string }>("/api/scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId: customer.id, mode: "dry_run" }),
      });
      await refreshState();
      toast({ title: "Dry run started", description: `Job ${data.jobId} started for ${customer.name}` });
    } catch (error) {
      const message = String(error?.message || error);
      if (message.includes("(404)")) {
        toast({
          title: "Dry run unavailable",
          description: "Public scan API does not support dry runs.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Dry run failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleCancelScan = (customer: Customer) => {
    const runningJob = jobs
      .filter((j) => j.customerId === customer.id && j.status === "running")
      .slice()
      .reverse()[0];

    if (!runningJob) {
      toast({ title: "No running job", description: `${customer.name} has no running scans.` });
      return;
    }

    fetchApi("/api/cancel", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ jobId: runningJob.id }),
    })
      .then(async (r) => {
        await assertApiOk(r, "Cancel");
        await refreshState();
        toast({
          title: "Scan cancelled",
          description: `Cancelled scan for ${customer.name}`,
          variant: "destructive",
        });
      })
      .catch((error) =>
        toast({
          title: "Cancel failed",
          description: String(error?.message || error),
          variant: "destructive",
        }),
      );
  };

  const activeCustomerFindings = useMemo(() => {
    if (!activeCustomerId) return [];
    const mine = scanResults.filter((r) => r.customerId === activeCustomerId);
    if (mine.length === 0) return [];
    const latest = mine
      .slice()
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
    if (!latest) return mine.flatMap((r) => r.findings);
    return mine.filter((r) => r.timestamp === latest.timestamp).flatMap((r) => r.findings);
  }, [activeCustomerId, scanResults]);

  const activeCustomerReportScanResults = useMemo(() => {
    if (!activeCustomerId) return [];
    const mine = scanResults.filter((r) => r.customerId === activeCustomerId);
    if (mine.length === 0) return [];

    const wpscanQuality = (result: ScanResult | undefined) => {
      if (!result) return -1000;
      let score = 0;
      for (const f of result.findings || []) {
        const t = String(f?.title || "").toLowerCase();
        if (t.startsWith("wordpress version detected:")) score += 100;
        if (t.startsWith("core:")) score += 120;
        if (t.startsWith("plugin detected:") || t.startsWith("plugin (")) score += 35;
        if (t.startsWith("theme detected:") || t.startsWith("theme (")) score += 35;
        if (t.includes("xml-rpc")) score += 15;
        if (t.includes("must use plugins")) score += 10;
      }
      // Penalize incomplete runs (often canceled): banner/headers only.
      if (score === 0 && (result.findings?.length ?? 0) <= 2) score = -200;
      return score;
    };

    const statusRank = (status: ScanResult["status"]) => {
      if (status === "success") return 2;
      if (status === "warning") return 1;
      return 0;
    };

    const groups = new Map<string, ScanResult[]>();
    for (const r of mine) {
      const key = String(r.timestamp || "");
      const arr = groups.get(key) || [];
      arr.push(r);
      groups.set(key, arr);
    }

    const groupRank = (arr: ScanResult[]) => {
      const t = new Date(arr[0]?.timestamp || 0).getTime();
      const recency = Number.isFinite(t) ? t : 0;
      const statusSum = arr.reduce((s, r) => s + statusRank(r.status), 0);
      const totalFindings = arr.reduce((s, r) => s + (r.findings?.length ?? 0), 0);
      const wpscan = arr.find((r) => r.scanner === "wpscan");
      const wpscanScore = wpscanQuality(wpscan);
      const hasWpscan = Boolean(wpscan);

      // Strongly prefer coherent runs with usable WPScan data.
      const base = statusSum * 1000 + totalFindings * 10 + wpscanScore;
      const penalty = hasWpscan ? 0 : 5000;
      return base - penalty + recency / 1e10;
    };

    const ranked = Array.from(groups.values()).sort((a, b) => groupRank(b) - groupRank(a));
    const best = ranked[0] || [];
    return best.slice().sort((a, b) => String(a.scanner).localeCompare(String(b.scanner)));
  }, [activeCustomerId, scanResults]);

  const activeCustomerReportFindings = useMemo(
    () => activeCustomerReportScanResults.flatMap((r) => r.findings),
    [activeCustomerReportScanResults],
  );

  const activeCustomerRunningJob = useMemo(() => {
    if (!activeCustomerId) return null;
    const running = jobs.filter((j) => j.customerId === activeCustomerId && j.status === "running");
    return running.length > 0 ? running[running.length - 1] : null;
  }, [activeCustomerId, jobs]);

  return (
    <>
      <Helmet>
        <title>Live Scan | ShadowScan Portal</title>
        <meta
          name="description"
          content="Run and manage Live Scan operations: trigger scans, watch live logs, and generate PDF reports."
        />
      </Helmet>
      
      <div className="flex flex-col gap-6">
        <Header
          isRunning={isRunning}
          startTime={formatDateTime(runStartedAt, timezone)}
          ipAddress={ipAddress}
          queueCount={queueCount}
          timezone={timezone}
          onTimezoneChange={setTimezone}
          onOpenSettings={() => setSettingsModalOpen(true)}
        />

        {debugApi && (
          <div className="text-xs text-muted-foreground">
            API base: {apiBase} · state: {apiStateUrl}
          </div>
        )}

        {!adminState && isLoading && (
          <div className="rounded-md border border-border/60 bg-background/60 p-4 text-sm text-muted-foreground">
            Loading autoscan data...
          </div>
        )}

        {!adminState && isError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            <div>Failed to load autoscan data.</div>
            <div className="mt-2 text-xs">{String((error as Error)?.message || error)}</div>
            <Button className="mt-3" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        )}

        {adminState && (
          <>
            <CustomerTable
              customers={customers}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onAdd={handleAdd}
              onRefresh={handleRefresh}
              onScanAll={handleScanAll}
              onDryRunAll={handleDryRunAll}
              onCancel={handleCancel}
              onClearAllResults={handleClearAllResults}
              onOpen={handleOpenCustomer}
              onMore={handleMoreCustomer}
              leadFilter={leadFilter}
              onLeadFilterChange={setLeadFilter}
              sortMode={sortMode}
              onSortModeChange={setSortMode}
              timezone={timezone}
            />

            <CustomerDetailModal
              customer={activeCustomer}
              open={detailModalOpen}
              onClose={() => setDetailModalOpen(false)}
              onScan={handleScan}
              onDryRun={handleDryRun}
              onCancel={handleCancelScan}
              onEdit={handleEditCustomer}
              pdfAvailable={Boolean(activeCustomerId && reportMetaByCustomerId[activeCustomerId]?.exists)}
              pdfGenerating={Boolean(activeCustomerId && pdfGeneratingByCustomerId[activeCustomerId])}
              onOpenPdf={(c) => openPdfReport(c.id)}
              onRemakePdf={(c) => generateAndUploadPdf(c, { force: true, openAfter: true })}
              onDelete={handleDeleteCustomer}
              onClearResults={handleClearCustomerResults}
              scanResults={scanResults}
              timezone={timezone}
              runningJob={activeCustomerRunningJob}
            />

            <CustomerEditModal
              customer={activeCustomer}
              open={editModalOpen}
              onClose={() => setEditModalOpen(false)}
              onSave={handleSaveCustomer}
              isNew={isNewCustomer}
            />

            <SettingsModal open={settingsModalOpen} onClose={() => setSettingsModalOpen(false)} />

            <ReportModal
              customer={activeCustomer}
              open={reportModalOpen}
              onClose={() => setReportModalOpen(false)}
              onOpenDetails={() => {
                if (!activeCustomer) return;
                setActiveCustomerId(activeCustomer.id);
                setReportModalOpen(false);
                setDetailModalOpen(true);
              }}
              pdfAvailable={Boolean(activeCustomerId && reportMetaByCustomerId[activeCustomerId]?.exists)}
              pdfGenerating={Boolean(activeCustomerId && pdfGeneratingByCustomerId[activeCustomerId])}
              onOpenPdf={() => {
                if (!activeCustomerId) return;
                openPdfReport(activeCustomerId);
              }}
              onRemakePdf={() => {
                if (!activeCustomer) return;
                generateAndUploadPdf(activeCustomer, { force: true, openAfter: true }).catch((error) =>
                  toast({
                    title: "PDF generation failed",
                    description: String(error?.message || error),
                    variant: "destructive",
                  }),
                );
              }}
              findings={activeCustomerReportFindings}
              scanResults={activeCustomerReportScanResults}
            />
          </>
        )}
      </div>
    </>
  );
};

export default Index;
